import { createSlice } from "@reduxjs/toolkit";
import undoable, { includeAction } from "redux-undo";
import { arrayRemove, changeSelectionOfSelectProps, createSectionFrom, getSectionToEdit, mapWidgetMetadataForSection, moveDown, moveUp, updateAnalysisSection, updateLayout, updateSectionWidgets } from "./designer-helper";
import { PageMargin, PageSize, PageType } from "components/workspaces/store/types";
import { isDashboardPageAction, isLocationChangeAction } from "store/current-dashboard/utils";
const initialState = { active: false, widgets: {} };
const removeWidget = (state, widgetId, sectionId) => {
    if (state.addedOrUpdatedWidgets && widgetId in state.addedOrUpdatedWidgets) {
        delete state.addedOrUpdatedWidgets[widgetId];
    }
    else {
        state.removedWidgets = { ...state.removedWidgets, [widgetId]: { sectionId } };
    }
};
export const getPageSize = (newPage) => (newPage.type === PageType.PRINT ? PageSize.A3 : PageSize.A4);
export const getPageType = (newPage) => (newPage.type === PageType.PRINT ? PageType.SCREEN : PageType.PRINT);
const slice = createSlice({
    extraReducers: (builder) => {
        builder.addMatcher((action) => isLocationChangeAction(action) && !isDashboardPageAction(action), (state) => Object.assign(state, { active: false }));
    },
    initialState,
    name: "designerReducers",
    reducers: {
        addNewSectionToAnalysis: (state, action) => {
            if (state.analysis && state.sections && state.analysis.sections) {
                state.analysis.sections.push(...action.payload);
                action.payload.forEach((s) => {
                    if (state.sections && s.id) {
                        state.sections[s.id] = s;
                        state.addedSections?.push(s.id);
                    }
                });
            }
        },
        addWidgetToSection: (state, action) => {
            const { editedSection, currentSectionIndex } = getSectionToEdit(state, action.payload.sectionId);
            // If there is a groupId, find the group widget and add the new widget to it
            if (action.payload.groupId && action.payload.widget.id &&
                state.widgets && state.widgets[action.payload.groupId]) {
                Object.assign(state.widgets[action.payload.groupId], {
                    componentProps: {
                        ...state.widgets[action.payload.groupId].componentProps,
                        widgets: [...(state.widgets[action.payload.groupId || ""]?.componentProps).widgets, action.payload.widget.id]
                    }
                });
                state.addedOrUpdatedWidgets = { ...state.addedOrUpdatedWidgets, [action.payload.groupId]: { sectionId: action.payload.sectionId } };
            }
            if (action.payload.widget.id) {
                updateSectionWidgets(editedSection, action.payload.conditionalVisibility, action.payload.widget.id, (widgetsSection) => {
                    action.payload.children?.forEach((child) => {
                        if (!widgetsSection.some((seclay) => seclay.id === child.id)) {
                            widgetsSection.push({ id: child.id });
                            if (state.widgets && child.id) {
                                const dataSelectedProps = changeSelectionOfSelectProps(child.componentProps.dataSelectProps, action.payload.childrenSelectedValues);
                                state.widgets[child.id] = {
                                    ...child,
                                    componentProps: { ...child.componentProps, dataSelectProps: [...dataSelectedProps], parentId: action.payload.widgetId },
                                    // Remove this metadata line of code when all configurations are updated from metaData to metadata
                                    metadata: { ...child.metadata || {}, ...child.metaData }
                                };
                                state.addedOrUpdatedWidgets = { ...state.addedOrUpdatedWidgets, [child.id]: { sectionId: action.payload.sectionId } };
                            }
                        }
                    });
                    const maxY = Math.max(0, ...widgetsSection
                        .map((seclay) => (seclay?.layouts?.[action.payload.currentViewport]?.positionY || 0) +
                        (seclay?.layouts?.[action.payload.currentViewport]?.height || 0)));
                    // If we are in a group then no need of layouts
                    widgetsSection.push({
                        id: action.payload.widget.id,
                        layouts: action.payload.groupId
                            ? {}
                            : {
                                [action.payload.currentViewport]: {
                                    height: action.payload.metaData.defaultDimension?.height,
                                    positionX: 0,
                                    positionY: maxY,
                                    width: action.payload.metaData.defaultDimension?.width
                                }
                            }
                    });
                    if (state.widgets && action.payload.widget.id) {
                        const dataSelectedProps = changeSelectionOfSelectProps(action.payload.widget.componentProps.dataSelectProps, action.payload.selectedValues);
                        let metadata = {};
                        if (Object.keys(action.payload.metaData).length) {
                            metadata = { ...action.payload.metaData, isDefault: false };
                        }
                        else {
                            metadata = { ...action.payload.widget.metaData, isDefault: false };
                        }
                        state.widgets[action.payload.widget.id] = {
                            ...action.payload.widget,
                            componentProps: {
                                ...action.payload.widget.componentProps,
                                childrenId: (action.payload.widget.componentProps).childrenId ? action.payload.children?.[0].id || "" : "",
                                dataSelectProps: [...dataSelectedProps],
                                default: (action.payload.widget.componentProps).default ? action.payload.children?.[0].id : "",
                                widgets: (action.payload.widget.componentProps).widgets ? action.payload.children?.map((child) => child.id) : []
                            },
                            metadata
                        };
                    }
                    mapWidgetMetadataForSection(state.widgets);
                    updateAnalysisSection(editedSection, state.analysis?.sections, currentSectionIndex);
                    state.addedOrUpdatedWidgets = { ...state.addedOrUpdatedWidgets, [action.payload.widget.id || ""]: { sectionId: action.payload.sectionId } };
                });
            }
        },
        closeDesignerMode: (state) => {
            Object.assign(state, { active: false, addedOrUpdatedWidgets: {}, analysis: {}, removedSections: [], removedWidgets: {}, sections: {}, widgets: {} });
        },
        duplicateSection: (state, action) => {
            const idToDuplicate = state.analysis?.sections?.[action.payload.index].id;
            if (idToDuplicate && state.sections && state.analysis?.sections) {
                const { section: newSection, widgets: newWidget } = createSectionFrom(state.sections[idToDuplicate], action.payload.widgets);
                let modifiedSection = {};
                const newPage = newSection;
                if (action.payload.changeType) {
                    modifiedSection = {
                        ...newPage,
                        isHorizontal: false,
                        margin: PageMargin.M_10MM,
                        pageSize: getPageSize(newPage),
                        type: getPageType(newPage)
                    };
                }
                state.sections[newSection.id] = action.payload.changeType ? modifiedSection : newSection;
                state.addedSections?.push(newSection.id);
                state.analysis.sections.splice(action.payload.index + 1, 0, {
                    ...modifiedSection,
                    id: newSection.id,
                    name: newSection.name,
                    requiredModules: newSection.requiredModules
                });
                state.widgets ??= {};
                state.addedOrUpdatedWidgets ??= {};
                newWidget.forEach((w) => {
                    if (w.id) {
                        state.widgets[w.id] = w;
                        state.addedOrUpdatedWidgets[w.id] = { sectionId: newSection.id };
                    }
                });
            }
        },
        moveSectionDown: (state, action) => {
            if (state.analysis?.sections) {
                moveDown(action.payload, state.analysis.sections);
            }
        },
        moveSectionUp: (state, action) => {
            if (state.analysis?.sections) {
                moveUp(action.payload, state.analysis.sections);
            }
        },
        removeSection: (state, action) => {
            if (state.analysis?.sections) {
                const sectionId = state.analysis.sections[action.payload]?.id;
                if (sectionId) {
                    const addedSectionIndex = state.addedSections?.findIndex((x) => x === sectionId) ?? -1;
                    addedSectionIndex === -1 ? state.removedSections?.push(sectionId) : state.addedSections?.splice(addedSectionIndex, 1);
                }
                arrayRemove(action.payload, state.analysis.sections);
            }
        },
        removeWidgetFromGroup: (state, action) => {
            if (state.widgets && action.payload.groupId && action.payload.widgetId) {
                const compProps = state.widgets[action.payload.groupId].componentProps;
                const remainingWidgets = compProps.widgets.filter((x) => x !== action.payload.widgetId);
                Object.assign(state.widgets[action.payload.groupId], {
                    componentProps: {
                        ...state.widgets[action.payload.groupId].componentProps,
                        // Reset default widget to the first one in the list, or null if the list is empty
                        default: remainingWidgets[0],
                        widgets: remainingWidgets
                    }
                });
                state.addedOrUpdatedWidgets = { ...state.addedOrUpdatedWidgets, [action.payload.groupId]: { sectionId: action.payload.sectionId } };
                removeWidget(state, action.payload.widgetId, action.payload.sectionId);
            }
        },
        removeWidgetFromSection: (state, action) => {
            const { editedSection, currentSectionIndex } = getSectionToEdit(state, action.payload.sectionId);
            updateSectionWidgets(editedSection, action.payload.conditionalVisibility, action.payload.widgetId, (widgetsSection, index) => {
                widgetsSection.splice(index, 1);
                updateAnalysisSection(editedSection, state.analysis?.sections, currentSectionIndex);
            });
            removeWidget(state, action.payload.widgetId, action.payload.sectionId);
        },
        reorderWidgetsInGroup: (state, action) => {
            if (state.widgets && action.payload.groupId && action.payload.newIndex !== action.payload.oldIndex) {
                const compProps = state.widgets[action.payload.groupId].componentProps;
                // Copy array, remove widgetId at old index and insert it at new index
                const newWidgets = [...compProps.widgets];
                newWidgets.splice(action.payload.newIndex, 0, newWidgets.splice(action.payload.oldIndex, 1)[0]);
                Object.assign(state.widgets[action.payload.groupId], {
                    componentProps: {
                        ...state.widgets[action.payload.groupId].componentProps,
                        // Reset default widget to the first one in the list, or null if the list is empty
                        default: newWidgets[0],
                        widgets: newWidgets
                    }
                });
            }
        },
        saveWidgetLayout: (state, action) => {
            const { editedSection, currentSectionIndex } = getSectionToEdit(state, action.payload.sectionId);
            action.payload.widgetsLayout.forEach((widgetLayout) => {
                updateSectionWidgets(editedSection, action.payload.conditionalVisibility, widgetLayout.i, (_widgetsSection, index, widgetSection) => {
                    if (editedSection && editedSection.id) {
                        const layouts = state.sections?.[editedSection.id].widgets?.[widgetSection]?.[index]?.layouts;
                        if (layouts && !layouts[action.payload.localBreakpoint]) {
                            layouts[action.payload.localBreakpoint] = {};
                        }
                        const layout = layouts?.[action.payload.localBreakpoint];
                        if (layout) {
                            updateLayout(layout, widgetLayout);
                            updateAnalysisSection(editedSection, state.analysis?.sections, currentSectionIndex);
                        }
                    }
                });
            });
        },
        setAnalysisName: (state, action) => {
            if (state.analysis) {
                state.analysis.name = action.payload;
            }
        },
        setDesignerMode: (state, action) => {
            Object.assign(state, { active: true, ...action.payload, addedSections: [], removedSections: [] });
        },
        setSectionName: (state, action) => {
            const { editedSection, currentSectionIndex } = getSectionToEdit(state, action.payload.sectionId);
            editedSection.name = action.payload.newName;
            const sectionInStateAnalysis = state?.analysis?.sections?.[currentSectionIndex];
            if (sectionInStateAnalysis) {
                sectionInStateAnalysis.name = action.payload.newName;
            }
        },
        updateSectionPreviewOptions: (state, action) => {
            const pageToUpdate = { ...state.sections?.[action.payload.pageId], ...action.payload.pageLayout };
            state.sections = {
                ...state.sections,
                [action.payload.pageId]: pageToUpdate
            };
        },
        updateWidget: (state, action) => {
            if (state.widgets && action.payload.id) {
                state.widgets[action.payload.id] = action.payload;
            }
        }
    }
});
export const designerReducers = undoable(slice.reducer, {
    filter: includeAction(Object.keys(slice.actions).map((action) => `${slice.name}/${action}`))
});
export const { addNewSectionToAnalysis, removeWidgetFromGroup, reorderWidgetsInGroup, updateWidget, addWidgetToSection, closeDesignerMode, duplicateSection, moveSectionDown, moveSectionUp, removeSection, setAnalysisName, setSectionName, setDesignerMode, removeWidgetFromSection, saveWidgetLayout, updateSectionPreviewOptions } = slice.actions;
